import * as React from "react";
import { Pressable, Text, View } from "react-native";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import { Link, Stack, Tabs, useRouter } from "expo-router";
import { BottomTabBarProps } from "@react-navigation/bottom-tabs";
import {
  BellIcon,
  HomeIcon,
  InboxIcon,
  PlusIcon,
  SearchIcon,
  UserIcon,
} from "lucide-react-native";

import { useAccountActions } from "~/components/AccountActions/context";
import { AccountSwitcher } from "~/components/AccountSwitcher";
import { StackHeaderLeft, StackHeaderRight } from "~/components/HeaderElements";
import { ResponsiveRenderer } from "~/components/ResponsiveRenderer";
import { backlogSlug } from "~/app/(app)/(authenticated)/[account]/(account)/backlog";
import Logo from "~/assets/logo.svg";
import { useHonoClient } from "~/hooks/HonoContext";
import { useCurrentAccount } from "~/hooks/useCurrentAccount";
import { icons, useHackyParams } from "~/lib/constants";
import { useAccountStore } from "~/stores/space";
import { Button } from "~/ui/Button";
import { Dropdown } from "~/ui/Dropdown";
import { TextInput } from "~/ui/TextInput";
import { AddNewMenu } from "../_layout";

export default function AccountLayout() {
  const { slug: accountSlug } = useCurrentAccount();
  return (
    <>
      <Stack.Screen
        options={{
          headerTitle: "",
          headerLeft: () => (
            <ResponsiveRenderer
              web={
                <StackHeaderLeft className="flex-row items-center gap-2">
                  <Link
                    href={{
                      pathname: "/(app)/(authenticated)/[account]/(account)",
                      params: {
                        account: accountSlug,
                      },
                    }}
                  >
                    <Logo className="size-6" />
                  </Link>
                  <AccountSwitcher />
                </StackHeaderLeft>
              }
              default={
                <StackHeaderLeft>
                  <AccountSwitcher />
                </StackHeaderLeft>
              }
            />
          ),
          headerRight: () => (
            <ResponsiveRenderer
              webDesktop={
                <StackHeaderRight className="flex-row items-center gap-2">
                  <TextInput placeholder="Search" size="small" />
                  <View className="border-r-divider-strong self-stretch border-r" />
                  <CustomMenu />
                  <Button
                    variant="outline"
                    size="small"
                    iconAfter={<InboxIcon size={18} className="text-icon" />}
                  />
                  <Button
                    variant="outline"
                    size="small"
                    iconAfter={<BellIcon size={18} className="text-icon" />}
                  />
                </StackHeaderRight>
              }
              default={
                <StackHeaderRight>
                  <Text className="text-icon">
                    <BellIcon size={24} />
                  </Text>
                </StackHeaderRight>
              }
            />
          ),
        }}
      />
      {/* TODO remove tabs when not in mobile view */}
      <Tabs
        screenOptions={{
          headerShown: false,
        }}
        tabBar={(props) => <CustomTabBar {...props} />}
        // detachInactiveScreens={false}
        sceneContainerStyle={{
          // paddingLeft: 8,
          // paddingRight: 8,
          // backgroundColor: colors.surface.primary,
          position: "relative",
        }}
      >
        <Tabs.Screen
          name="index"
          options={{
            tabBarIcon(props) {
              return (
                <TabBarIcon
                  icon={<HomeIcon size={24} />}
                  isFocused={props.focused}
                />
              );
            },
          }}
        />
        <Tabs.Screen
          name="search"
          options={{
            tabBarIcon(props) {
              return (
                <TabBarIcon
                  icon={<SearchIcon size={24} />}
                  isFocused={props.focused}
                />
              );
            },
          }}
        />
        <Tabs.Screen
          name={backlogSlug}
          options={{
            tabBarIcon(props) {
              return (
                <TabBarIcon
                  icon={<InboxIcon size={24} />}
                  isFocused={props.focused}
                />
              );
            },
          }}
        />
        <Tabs.Screen
          name="settings"
          options={{
            tabBarIcon(props) {
              return (
                <TabBarIcon
                  icon={<UserIcon size={24} />}
                  isFocused={props.focused}
                />
              );
            },
          }}
        />
      </Tabs>
    </>
  );
}

function CustomTabBar(props: BottomTabBarProps) {
  const { push } = useRouter();
  const { slug } = useCurrentAccount();
  const { state, navigation, descriptors, insets } = props;
  const tabs = state.routes.map((route, index) => {
    const { options } = descriptors[route.key];
    const label =
      options.tabBarLabel !== undefined
        ? options.tabBarLabel
        : options.title !== undefined
          ? options.title
          : route.name;

    const isFocused = state.index === index;

    const onPress = () => {
      const event = navigation.emit({
        type: "tabPress",
        target: route.key,
        canPreventDefault: true,
      });

      if (!isFocused && !event.defaultPrevented) {
        if (route.name === backlogSlug) {
          // backlog is a special case.
          // we cant render backlog tab component and replace the screen cuz that replaces the tab navigator
          // cuz of that there's no screen to go back to
          // so instead we never render backlog component and just push to the backlog space screen
          push({
            pathname: "/(app)/(authenticated)/[account]/(space)/[space]/saves",
            params: {
              account: slug,
              space: backlogSlug,
            },
          });
        } else {
          navigation.navigate(route.name, route.params);
        }
      }
    };

    const onLongPress = () => {
      navigation.emit({
        type: "tabLongPress",
        target: route.key,
      });
    };

    return (
      <Pressable
        key={route.key}
        accessibilityRole="button"
        accessibilityState={isFocused ? { selected: true } : {}}
        accessibilityLabel={options.tabBarAccessibilityLabel || String(label)}
        onPress={onPress}
        onLongPress={onLongPress}
        style={{
          padding: 4,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {options.tabBarIcon && (
          <options.tabBarIcon
            focused={isFocused}
            color="" // required in types but ignored
            size={10} // required in types but ignored
          />
        )}
      </Pressable>
    );
  });

  return (
    <ResponsiveRenderer
      webDesktop={null}
      default={
        <View
          style={{
            paddingBottom: insets.bottom,
            paddingLeft: insets.left,
            paddingRight: insets.right,
          }}
          className="border-divider-subtle sticky bottom-0 border-t"
        >
          <View className="flex-row items-center justify-between px-7 web:pb-2">
            {tabs[0]}
            {tabs[1]}
            <AddNewMenu />
            {tabs[2]}
            {tabs[3]}
          </View>
        </View>
      }
    />
  );
}

function TabBarIcon(props: { icon: React.ReactElement; isFocused: boolean }) {
  return (
    <View className="relative p-2">
      <Text className={props.isFocused ? "text-icon" : "text-input-text"}>
        {props.icon}
      </Text>
      {props.isFocused && (
        <View className="absolute -bottom-px left-0 right-0 items-center">
          <View className="bg-icon h-1 w-1 rounded-full" />
        </View>
      )}
    </View>
  );
}

function CustomMenu() {
  const accountActions = useAccountActions();
  const { store: account } = useAccountStore();

  const params = useHackyParams<{ space?: string }>();
  const space = params.space ? account.spaceBySlug(params.space) : null;
  const { hc } = useHonoClient();
  return (
    <>
      <Dropdown
        asChild
        trigger={
          <Button
            size="small"
            className="size-8 p-1"
            iconAfter={<PlusIcon size={24} />}
          />
        }
        groups={[
          {
            title: "workspace",
            items: [
              {
                key: "workspace",
                title: "Workspace",
                icon: icons.workspace,
                onSelect: () => {
                  accountActions.openAddSpace();
                },
              },
            ],
          },

          {
            title: "collection",
            items: [
              {
                key: "collection",
                title: "Collection",
                icon: icons.collection,
                onSelect() {
                  accountActions.openAddCollection();
                },
              },
            ],
          },
          {
            items: [
              // {
              //   key: "note",
              //   title: "Note",
              //   icon: {
              //     web: "edit",
              //     ios: "pencil",
              //   },
              // },
              {
                key: "media",
                title: "Media",
                icon: icons.media,
                async onSelect() {
                  try {
                    await ImagePicker.requestMediaLibraryPermissionsAsync();
                    const result = await ImagePicker.launchImageLibraryAsync({
                      mediaTypes: ImagePicker.MediaTypeOptions.All,
                      presentationStyle:
                        ImagePicker.UIImagePickerPresentationStyle.FORM_SHEET,
                    });

                    if (!result.canceled) {
                      const asset = result.assets[0];

                      const data = await hc.accounts[":accountId"].object
                        .$put({
                          param: {
                            accountId: account.id,
                          },
                          json: {
                            file: {
                              data: asset.uri,
                              name: asset.fileName ?? "",
                              type: asset.mimeType ?? asset.type ?? "image",
                            },
                          },
                        })
                        .then((res) => res.json());

                      accountActions.openAddSave({
                        spaceId: space?.id,
                        type: "MEDIA",
                        mediaMeta: {
                          assetId: asset.assetId ?? "",
                          uri: asset.uri,
                          type: asset.type ?? "image",
                          duration: asset.duration ?? 0,
                          fileName: asset.fileName ?? "",
                          fileSize: asset.fileSize ?? 0,
                          height: asset.height,
                          width: asset.width,
                        },
                      });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                },
              },
              {
                key: "file",
                title: "File",
                icon: icons.file,

                async onSelect() {
                  try {
                    const result = await DocumentPicker.getDocumentAsync();

                    if (!result.canceled) {
                      const asset = result.assets[0];
                      accountActions.openAddSave({
                        spaceId: space?.id,

                        type: "FILE",
                        fileMeta: {
                          name: asset?.name ?? "",
                          uri: asset?.uri ?? "",
                          mimeType: asset?.mimeType ?? "",
                          size: asset?.size ?? 0,
                          file: asset.file,
                        },
                      });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                },
              },
              {
                key: "link",
                title: "Link",
                icon: icons.link,
                onSelect() {
                  accountActions.openAddSave({
                    spaceId: space?.id,

                    type: "URL",
                  });
                },
              },
            ],
          },
        ]}
      />
    </>
  );
}
